import { useMemo, type CSSProperties } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, RichTextContent, useContentToken, useTokens } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

// ============================================================================
// Component
// ============================================================================
//
// The employer list is bespoke per property, so we can't source it from the
// property feed or a shared collection. Instead we expose 12 text slots —
// editors fill in what they need and leave the rest blank; empty slots are
// hidden at render time so the grid stays visually clean. Twelve is a round
// number that fills a three-column desktop grid with four rows before the
// section starts to feel long; if a property needs more, add slots here
// (both a new `useContentToken` call above AND a matching schema entry).

/**
 * Preferred Employer name grid: a heading + optional intro line, followed by
 * a grid of employer names the property has partnered with. Individual slots
 * with no override are hidden, so the visible grid always looks intentional
 * even before an editor fills every slot.
 */
export function PreferredEmployerList() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLUListElement>();

  // One useContentToken call per slot — hooks must be called at the top
  // level, not inside a loop, so the array length stays a compile-time
  // constant. Keep this list in sync with the tokens declared below.
  const e1 = useContentToken('preferredemployerlist.employer1');
  const e2 = useContentToken('preferredemployerlist.employer2');
  const e3 = useContentToken('preferredemployerlist.employer3');
  const e4 = useContentToken('preferredemployerlist.employer4');
  const e5 = useContentToken('preferredemployerlist.employer5');
  const e6 = useContentToken('preferredemployerlist.employer6');
  const e7 = useContentToken('preferredemployerlist.employer7');
  const e8 = useContentToken('preferredemployerlist.employer8');
  const e9 = useContentToken('preferredemployerlist.employer9');
  const e10 = useContentToken('preferredemployerlist.employer10');
  const e11 = useContentToken('preferredemployerlist.employer11');
  const e12 = useContentToken('preferredemployerlist.employer12');

  const slots: { path: string; name: string }[] = useMemo(
    () =>
      [
        { path: 'preferredemployerlist.employer1', name: (e1 || '').trim() },
        { path: 'preferredemployerlist.employer2', name: (e2 || '').trim() },
        { path: 'preferredemployerlist.employer3', name: (e3 || '').trim() },
        { path: 'preferredemployerlist.employer4', name: (e4 || '').trim() },
        { path: 'preferredemployerlist.employer5', name: (e5 || '').trim() },
        { path: 'preferredemployerlist.employer6', name: (e6 || '').trim() },
        { path: 'preferredemployerlist.employer7', name: (e7 || '').trim() },
        { path: 'preferredemployerlist.employer8', name: (e8 || '').trim() },
        { path: 'preferredemployerlist.employer9', name: (e9 || '').trim() },
        { path: 'preferredemployerlist.employer10', name: (e10 || '').trim() },
        { path: 'preferredemployerlist.employer11', name: (e11 || '').trim() },
        { path: 'preferredemployerlist.employer12', name: (e12 || '').trim() },
      ],
    [e1, e2, e3, e4, e5, e6, e7, e8, e9, e10, e11, e12],
  );
  const filledSlots = useMemo(() => slots.filter((s) => s.name.length > 0), [slots]);

  const tokens = useTokens();
  // Chip color: same "themed-text-or-fallback" pattern used by the Benefits
  // section, applied against `--vibe-color-surface` since that's the chip
  // background.
  const chipStyle = useMemo<CSSProperties | undefined>(() => {
    const surface = tokens?.color?.surface;
    if (!surface) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(surface, themed) >= 4.5 ? themed : getContrastTextColor(surface);
    return { ['--pel-chip-text' as string]: color } as CSSProperties;
  }, [tokens]);

  return (
    <section
      className="w-full bg-[var(--vibe-color-background)] pt-4 pb-12 md:pt-6 md:pb-20"
      style={chipStyle}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div ref={headingRef} className="reveal-fade-up text-center mb-10 md:mb-14">
          <RichTextContent
            path="preferredemployerlist.heading"
            label="Heading"
            defaultValue="<p>Our Preferred Employer Partners</p>"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold uppercase tracking-wide leading-[1.1] mb-4"
            style={{ color: '#000' }}
          />

          <RichTextContent
            path="preferredemployerlist.subheading"
            label="Subheading"
            defaultValue="<p>We&rsquo;re proud to partner with the following local employers. If you work for one of these organizations, you qualify for our full slate of Preferred Employer benefits.</p>"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] max-w-3xl mx-auto [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        {/* Employer grid — chips of names. Blank slots are hidden. */}
        {filledSlots.length > 0 ? (
          <ul
            ref={gridRef}
            className="reveal-fade-up grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          >
            {filledSlots.map((slot) => (
              <li key={slot.path} className="pel-chip">
                <EditableToken path={slot.path} as="span" className="pel-chip-label">
                  {slot.name}
                </EditableToken>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center font-body text-sm italic text-[var(--vibe-color-text-muted)]">
            Add employer names in the editor to populate this list.
          </p>
        )}

        {/* Optional closing note (e.g. "Don't see your employer? Ask us how to be added."). */}
        <div className="mt-10 md:mt-12 text-center">
          <RichTextContent
            path="preferredemployerlist.note"
            label="Closing Note"
            defaultValue='<p>Don&rsquo;t see your employer here? <a href="/contact-us">Contact our leasing team</a> — we&rsquo;re always growing our partner network.</p>'
            as="div"
            className="font-body text-sm italic leading-relaxed text-[var(--vibe-color-text-muted)] max-w-2xl mx-auto [&_a]:text-primary [&_a]:underline"
          />
        </div>

        {/* Schema-lint anchors — hidden, never interactive. Static string
            paths are REQUIRED by the schema linter (which extracts wrappers
            with regex, not evaluation), so each slot gets its own literal
            <EditableToken> here even though the visible loop above uses
            dynamic paths. */}
        <div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
          <EditableToken path="preferredemployerlist.employer1" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer2" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer3" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer4" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer5" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer6" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer7" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer8" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer9" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer10" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer11" as="span">{''}</EditableToken>
          <EditableToken path="preferredemployerlist.employer12" as="span">{''}</EditableToken>
        </div>
      </div>

      <style>{`
        .pel-chip {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 1rem;
          background: var(--vibe-color-surface);
          border: 1px solid color-mix(in srgb, var(--vibe-color-primary) 20%, transparent);
          border-radius: 0.5rem;
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
        }
        .pel-chip:hover {
          transform: translateY(-1px);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 55%, transparent);
          box-shadow: 0 4px 14px color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
        }
        .pel-chip-label {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 500;
          letter-spacing: 0.02em;
          line-height: 1.3;
          color: color-mix(in srgb, var(--pel-chip-text, var(--vibe-color-text)) 100%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerList',
  displayName: 'Preferred Employer List',
  tokens: {
    'preferredemployerlist.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Our Preferred Employer Partners</p>',
    },
    'preferredemployerlist.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue:
        '<p>We&rsquo;re proud to partner with the following local employers. If you work for one of these organizations, you qualify for our full slate of Preferred Employer benefits.</p>',
    },
    'preferredemployerlist.note': {
      label: 'Closing Note',
      type: 'richtext',
      defaultValue:
        '<p>Don&rsquo;t see your employer here? <a href="/contact-us">Contact our leasing team</a> — we&rsquo;re always growing our partner network.</p>',
    },
    'preferredemployerlist.employer1': {
      label: 'Employer 1',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer2': {
      label: 'Employer 2',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer3': {
      label: 'Employer 3',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer4': {
      label: 'Employer 4',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer5': {
      label: 'Employer 5',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer6': {
      label: 'Employer 6',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer7': {
      label: 'Employer 7',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer8': {
      label: 'Employer 8',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer9': {
      label: 'Employer 9',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer10': {
      label: 'Employer 10',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer11': {
      label: 'Employer 11',
      type: 'text',
      defaultValue: '',
    },
    'preferredemployerlist.employer12': {
      label: 'Employer 12',
      type: 'text',
      defaultValue: '',
    },
  },
};

(PreferredEmployerList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerList;
